import { absoluteUrl, site } from './site.js';

/**
 * Page metadata, built once so every route describes itself the same way.
 *
 * TanStack Router's `head` takes flat `meta` and `links` arrays. Writing those
 * by hand per route means a page eventually ships with a canonical pointing at
 * the wrong path, or an OpenGraph title that stopped matching the `<title>`
 * three edits ago. This builds the whole set from the two or three facts a page
 * actually knows about itself.
 */

export type SeoInput = {
  /** Without the " · ReviewsJet" suffix — this adds it. */
  title: string;
  description: string;
  /** Site-root-relative, e.g. `/reviews/google`. Becomes the canonical. */
  path: string;
  /** Absolute or root-relative. Falls back to the site card. */
  image?: string;
  /** `article` for blog posts; everything else is a `website`. */
  type?: 'website' | 'article';
  /**
   * Keep the page out of the index.
   *
   * For pages that exist to be linked to rather than found — a generator's
   * shared-state URL, a thank-you page. Not a substitute for deciding whether a
   * page should exist.
   */
  noIndex?: boolean;
  /**
   * Emit `<link rel="canonical">`. On for every page; off for the root route.
   *
   * TanStack merges the `links` of every matched route by concatenating them
   * and dropping only exactly-equal duplicates — there is no last-one-wins for
   * `rel`. So a canonical in the root's defaults is not a fallback the page
   * overrides: it is a *second* canonical, pointing at `/`, in front of the
   * page's own on every URL but the homepage. Two disagreeing canonicals is
   * the one thing Google resolves by ignoring both and choosing for itself,
   * which is what Search Console reports as "Duplicate, Google chose different
   * canonical than user".
   *
   * The root cannot emit a correct one — it does not know the URL — so it
   * emits none, and every page states its own.
   */
  canonical?: boolean;
};

export type HeadTag = Record<string, string | boolean | undefined>;

export type Head = {
  meta: HeadTag[];
  links: HeadTag[];
};

/**
 * The title as it appears in a tab and in a search result.
 *
 * Suffixed rather than templated in the root route, because the homepage is the
 * one page whose title should not read "ReviewsJet · ReviewsJet".
 */
export function pageTitle(title: string): string {
  return title === site.name ? title : `${title} · ${site.name}`;
}

export function seo({
  title,
  description,
  path,
  image,
  type = 'website',
  noIndex = false,
  canonical = true,
}: SeoInput): Head {
  const url = absoluteUrl(path);
  const card = image ? absoluteUrl(image) : absoluteUrl('/og/reviewsjet.png');
  const fullTitle = pageTitle(title);

  return {
    meta: [
      { title: fullTitle },
      { name: 'description', content: description },

      /*
        Robots is emitted only when it says something. `index, follow` is the
        default behaviour, and a tag that restates the default is a tag someone
        will later "fix" by editing it.
      */
      ...(noIndex ? [{ name: 'robots', content: 'noindex, nofollow' }] : []),

      { property: 'og:type', content: type },
      { property: 'og:site_name', content: site.name },
      { property: 'og:title', content: fullTitle },
      { property: 'og:description', content: description },
      { property: 'og:url', content: url },
      { property: 'og:image', content: card },

      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: site.twitter },
      { name: 'twitter:title', content: fullTitle },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: card },
    ],
    links: canonical ? [{ rel: 'canonical', href: url }] : [],
  };
}

/**
 * A breadcrumb trail, as markup and as structured data.
 *
 * Both, because they answer different readers and neither substitutes for the
 * other: the list is how a visitor who arrived on a deep page from search
 * understands where they are, and the JSON-LD is how that trail shows up under
 * the result they clicked. A page that emits the schema without drawing the
 * trail is describing navigation it does not have.
 */
export type Crumb = { name: string; path: string };

export function breadcrumbSchema(crumbs: Crumb[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  };
}

/**
 * FAQ structured data, from the same array the page renders.
 *
 * One helper rather than the object literal four routes were each writing out,
 * because the risk here is not verbosity — it is that a page emits FAQ schema
 * for questions a visitor cannot find on it, which is the misuse Google
 * penalises. Passing the rendered array is the only way to guarantee the two
 * agree, so this takes the array and nothing else.
 *
 * Callers must render every question they pass.
 */
export function faqSchema(faqs: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: { '@type': 'Answer', text: faq.a },
    })),
  };
}

/**
 * ReviewsJet itself, as an organisation.
 *
 * Deliberately narrow. `SoftwareApplication` with an `aggregateRating` is the
 * schema this kind of page reaches for, and it is the one to be careful with:
 * a rating in structured data is a claim that a specific number of people gave
 * a specific average score, and it must come from ratings actually shown on the
 * page. Nothing here invents one — a page that displays real ReviewsJet reviews
 * can add the rating itself, from the same numbers it rendered.
 */
export function organisationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: site.name,
    url: site.url,
    logo: absoluteUrl('/reviewsjet.svg'),
    sameAs: ['https://twitter.com/ReviewsJetHQ'],
  };
}

/** Serialise a schema object for a `<script type="application/ld+json">`. */
export function jsonLd(schema: unknown): string {
  /*
    `<` is escaped because this string goes inside a script element: a `</` in
    any value — a competitor's quoted copy, a review someone wrote — closes the
    script early and drops the rest of the page's markup into the document as
    text. The other escapes cover the same class of trick.
  */
  return JSON.stringify(schema)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}
